import { Component, DestroyRef, ElementRef, inject, signal, viewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { filter } from 'rxjs/operators';
import { Location } from '@core/services/location';
import { Flipper } from '@flipper';
import { Breadcrumb } from '@layout/breadcrumb/breadcrumb';
import { ChatIntegrationComponent } from '@layout/chat-integration/chat-integration';
import { Footer } from '@layout/footer/footer';
import { Header } from '@layout/header/header';
import { MainNavigationMobile } from '@layout/main-navigation-mobile/main-navigation-mobile';
import { SystemMessages } from '@layout/system-messages/system-messages';
import { TranslatePipe } from '@ngx-translate/core';
import { ToTheTopLink } from '@shared/ui/to-the-top-link/to-the-top-link';
import { MatomoTrackerDirective } from 'ngx-matomo-client';

@Component({
  selector: 'app-root',
  templateUrl: './app.html',
  styleUrl: './app.scss',
  imports: [
    Header,
    MainNavigationMobile,
    Breadcrumb,
    RouterOutlet,
    ToTheTopLink,
    Footer,
    ChatIntegrationComponent,
    TranslatePipe,
    MatomoTrackerDirective,
    SystemMessages
  ]
})
export class App {
  private readonly router = inject(Router);
  private readonly activatedRoute = inject(ActivatedRoute);
  private readonly locationService = inject(Location);
  private readonly flipperService = inject(Flipper);

  private readonly initialTabFocus = viewChild.required<ElementRef<HTMLDivElement>>('initialTabFocus');
  private readonly destroyRef = inject(DestroyRef);

  public readonly sidebarMenuExpanded = signal<boolean>(false);
  public readonly hideBreadcrumbs = signal<boolean>(false);

  constructor() {
    this.flipperService.evaluate$().pipe(takeUntilDestroyed(this.destroyRef)).subscribe();

    this.router.events
      .pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({
        next: () => {
          // Reset tab focus when route changes
          if (!this.router.url.endsWith('#main')) {
            this.initialTabFocus().nativeElement.focus();
          }
          // Find the last activated route
          let route = this.activatedRoute;
          while (route.firstChild) {
            route = route.firstChild;
          }
          // Check if the route's component is the 404 page component and hide breadcrumbs if so
          this.hideBreadcrumbs.set(route.routeConfig?.path === '**');
        }
      });
  }

  onSidebarMenuExpanded($event: boolean) {
    this.sidebarMenuExpanded.set($event);
  }

  skipToContentLink(event: MouseEvent) {
    event.preventDefault();
    this.locationService.hash = '';
    setTimeout(() => {
      this.locationService.hash = 'main';
    });
  }
}
